/**
 * **数据同步状态 · 平台优先口径**（2026-10-04 第十批）。
 *
 * 用户原话：「数据同步这功能现在完全是没用的，能不能在这里，**按照平台显示同步状态**，
 * 而不是**店铺在第一层**」——因此本文件把「平台 → 频率配置 → 各店铺的历史回填 /
 * 最近 7 天覆盖」这条链路的**全部判定规则**收敛成零依赖纯函数，供
 * `@ams/store`（读库组装）、`apps/api`（只读端点）、`apps/web`（渲染）共用。
 * 判定**不写在模板里**，也不写在 SQL 里：否则同一份规则会有三份实现，迟早不一致。
 *
 * ## 三条纪律
 *
 *  1. **只判定，不取数**：本文件不认识数据库 / HTTP / 环境变量；输入是已经数出来的
 *     行数、日期与运行状态，输出是健康档 + 可展示的问题清单。
 *  2. **空缺 ≠ 0**：`rows` 是真实计数（0 就是 0）；`startDate/endDate` 用 `null` 表示
 *     「没有数据」，**绝不**折成某个哨兵日期。缺失日一律来自「应有日期 − 实有日期」。
 *  3. **频率配置是静态镜像**：`SYNC_PLATFORM_SCHEDULES` 逐字镜像 systemd timer 的真实
 *     `OnCalendar`（见 `ON_CALENDAR_SOURCE`）。**改 timer 时必须同步改这里**，
 *     否则页面会显示一个不存在的同步节奏。没有权限稳定读取 `systemctl show`
 *     时不假装有 scheduler 状态——端点只用库里的真实运行/数据时间戳。
 */

import { recentBusinessDates, shiftIsoDate } from "./ad-tree-history.js";
import type { IsoDate } from "./metrics.js";

/** 三个平台（固定顺序 = 页面展示顺序）。 */
export const SYNC_PLATFORMS: readonly SyncPlatform[] = ["meta", "google", "shopify"];

export type SyncPlatform = "meta" | "google" | "shopify";

/** 同步节奏。`hourly` 最高频，`manual` 表示没有定时器（只能手动触发）。 */
export type SyncCadence = "hourly" | "4x_daily" | "daily" | "manual";

/** 平台健康档。`ok` 为唯一「无问题」档，其余四档都必须在折叠行上给出提示。 */
export type SyncPlatformHealth = "ok" | "partial" | "stale" | "missing" | "error";

/**
 * 健康档的全序（用于「平台健康 = 最差店铺/作业」）。数值只用于比较，**不参与展示**。
 * 顺序：error > missing > stale > partial > ok。
 */
export const SYNC_HEALTH_SEVERITY: Readonly<Record<SyncPlatformHealth, number>> = {
  ok: 0,
  partial: 1,
  stale: 2,
  missing: 3,
  error: 4,
};

export function worstSyncHealth(left: SyncPlatformHealth, right: SyncPlatformHealth): SyncPlatformHealth {
  return SYNC_HEALTH_SEVERITY[left] >= SYNC_HEALTH_SEVERITY[right] ? left : right;
}

// ---------------------------------------------------------------------------
// 频率配置（静态镜像 systemd timer）
// ---------------------------------------------------------------------------

/**
 * `OnCalendar` 的真实来源（2026-10-04 生产实测）：
 *
 * ```
 * ams-meta-insights.timer      OnCalendar=*-*-* 00,06,12,18:40:00   # 每天 4 次
 * ams-meta-catalog.timer       OnCalendar=*-*-* 04:15:00            # 目录
 * ams-google-sync.timer        OnCalendar=*-*-* 05:20:00
 * ams-shopify-orders.timer     OnCalendar=*-*-* *:20:00             # 每小时 :20
 * ams-shopify-sync.timer       OnCalendar=*-*-* 03:00:00
 * ams-fx-sync.timer            OnCalendar=*-*-* 06:05:00
 * ams-fx-reconvert.timer       OnCalendar=*-*-* 06:33:00
 * ams-credential-health.timer  OnCalendar=*-*-* 00,06,12,18:10:00
 * ```
 *
 * ⚠️ **改 timer 时必须同步改本表**（这是唯一允许的第二份真相，且被单测钉住形状）。
 * 之所以不优先读 `systemctl show`：那会让只读端点依赖特权命令与 systemd 运行时，
 * 在容器 / 测试环境直接变成脆弱点。端点因此**不假装有 scheduler 状态**，
 * `lastRunAtIso` 只来自库里的真实运行台账 / 数据写入时间戳。
 */
export const ON_CALENDAR_SOURCE = "systemd timer（生产实测，2026-10-04）";

/** 单个同步作业的调度（`onCalendar` 逐字镜像 systemd）。 */
export interface SyncJobSchedule {
  /** systemd unit 名（去掉 `.timer`）。 */
  readonly unit: string;
  /** 该作业在页面里的中文一句话。 */
  readonly what: string;
  /** systemd `OnCalendar=` 原文。 */
  readonly onCalendar: string;
}

export interface SyncPlatformSchedule {
  readonly platform: SyncPlatform;
  readonly label: string;
  /** 该平台**最高频**作业的节奏（折叠行的 cadence）。 */
  readonly cadence: SyncCadence;
  /** `timer` = 有 systemd timer；`config` = 由配置/手动驱动。 */
  readonly source: "timer" | "config";
  readonly jobs: readonly SyncJobSchedule[];
}

/** 平台标签（页面第一层显示）。 */
export const SYNC_PLATFORM_LABELS: Readonly<Record<SyncPlatform, string>> = {
  meta: "Meta",
  google: "Google",
  shopify: "Shopify",
};

export const SYNC_PLATFORM_SCHEDULES: Readonly<Record<SyncPlatform, SyncPlatformSchedule>> = {
  meta: {
    platform: "meta",
    label: SYNC_PLATFORM_LABELS.meta,
    cadence: "4x_daily",
    source: "timer",
    jobs: [
      { unit: "ams-meta-insights", what: "Meta 广告 insights（日事实 + 7 日滚动 + 结算）", onCalendar: "*-*-* 00,06,12,18:40:00" },
      { unit: "ams-meta-catalog", what: "Meta 实体目录（账户 / 系列 / 广告组 / 广告）", onCalendar: "*-*-* 04:15:00" },
    ],
  },
  google: {
    platform: "google",
    label: SYNC_PLATFORM_LABELS.google,
    cadence: "daily",
    source: "timer",
    jobs: [{ unit: "ams-google-sync", what: "Google Ads campaign 日事实", onCalendar: "*-*-* 05:20:00" }],
  },
  shopify: {
    platform: "shopify",
    label: SYNC_PLATFORM_LABELS.shopify,
    cadence: "hourly",
    source: "timer",
    jobs: [
      { unit: "ams-shopify-orders", what: "Shopify 订单增量", onCalendar: "*-*-* *:20:00" },
      { unit: "ams-shopify-sync", what: "Shopify 目录 / 日指标", onCalendar: "*-*-* 03:00:00" },
    ],
  },
};

const CADENCE_RANK: Readonly<Record<SyncCadence, number>> = {
  hourly: 3,
  "4x_daily": 2,
  daily: 1,
  manual: 0,
};

const MINUTE_PAD = (value: number): string => String(value).padStart(2, "0");

interface ParsedOnCalendar {
  readonly kind: "hourly" | "timed" | "unparsed";
  readonly times: readonly string[];
  readonly minute: string;
}

/**
 * 解析本表用到的两种 systemd `OnCalendar` 形态：
 *  - `*-*-* *:MM:SS`    → 每小时（`times = []`，`minute = MM`）
 *  - `*-*-* HH,HH:MM:SS` → 每天固定时刻（`times = ["HH:MM", …]`，升序）
 *
 * 解析不了就返回 `unparsed`：**不猜**。展示层把它显示为原文而不是编一个节奏。
 */
export function parseOnCalendar(spec: string): ParsedOnCalendar {
  const match = /^\*-\*-\* (.*):(\d{2}):(\d{2})$/.exec(spec.trim());
  if (match === null) return { kind: "unparsed", times: [], minute: "" };
  const hours = match[1] ?? "";
  const minute = match[2] ?? "";
  if (hours === "*") return { kind: "hourly", times: [], minute };
  const parts = hours.split(",");
  if (parts.length === 0 || parts.some((part) => !/^\d{1,2}$/.test(part))) {
    return { kind: "unparsed", times: [], minute };
  }
  const times = parts
    .map((part) => `${MINUTE_PAD(Number(part))}:${minute}`)
    .sort((left, right) => left.localeCompare(right));
  return { kind: "timed", times, minute };
}

/** `hourly` / `4x_daily` / `daily` / `manual` —— 由 `OnCalendar` 纯推导。 */
export function syncJobCadence(onCalendar: string): SyncCadence {
  const parsed = parseOnCalendar(onCalendar);
  if (parsed.kind === "hourly") return "hourly";
  if (parsed.kind === "timed") return parsed.times.length > 1 ? "4x_daily" : "daily";
  return "manual";
}

/** 把 `HH:MM` 列表格式化成「每天 N 次（A/B/C）」；0 项 → `null`。 */
export function formatSyncTimes(times: readonly string[]): string | null {
  if (times.length === 0) return null;
  if (times.length === 1) return `每天 ${times[0]}`;
  return `每天 ${times.length} 次（${times.join(" / ")}）`;
}

/** 单个作业的展示文本，例如「每小时 :20」「每天 04:15」「每天 4 次（00:40 / …）」。 */
export function describeSyncJob(job: SyncJobSchedule): string {
  const parsed = parseOnCalendar(job.onCalendar);
  if (parsed.kind === "hourly") return `每小时 :${parsed.minute}`;
  const formatted = formatSyncTimes(parsed.times);
  return formatted ?? `手动（${job.onCalendar}）`;
}

/** 平台级节奏（最高频作业）与一句话展示。 */
export function describeSyncFrequency(schedule: SyncPlatformSchedule): {
  readonly cadence: SyncCadence;
  readonly display: string;
  readonly source: "timer" | "config";
} {
  let primary: SyncJobSchedule | null = null;
  let primaryCadence: SyncCadence = "manual";
  for (const job of schedule.jobs) {
    const cadence = syncJobCadence(job.onCalendar);
    if (primary === null || CADENCE_RANK[cadence] > CADENCE_RANK[primaryCadence]) {
      primary = job;
      primaryCadence = cadence;
    }
  }
  return {
    cadence: primaryCadence,
    display: primary === null ? "手动触发" : describeSyncJob(primary),
    source: schedule.source,
  };
}

// ---------------------------------------------------------------------------
// 日期骨架（UTC 纯算术，不受进程时区影响）
// ---------------------------------------------------------------------------

/** `YYYY-MM-DD` 是否是可解析的 ISO 日期。 */
export function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * 在指定 IANA 时区里，把 ISO 时刻换算成**业务日**（`YYYY-MM-DD`）。
 *
 * 时区边界的唯一处理点：`Intl.DateTimeFormat` 按目标时区取年月日，
 * **绝不**用 `new Date(...).toISOString().slice(0,10)`（那等于按 UTC 切日，
 * 跨零点就会差一天）。非法时刻 → `null`（未知不是今天）。
 */
export function businessDateInTimezone(iso: string, timeZone: string): IsoDate | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(parsed);
    return isIsoDate(parts) ? parts : null;
  } catch {
    return null;
  }
}

/** `to - from` 的整天数（`YYYY-MM-DD`，UTC 算术）；任一非法 → `null`。 */
export function daysBetweenIso(from: IsoDate, to: IsoDate): number | null {
  if (!isIsoDate(from) || !isIsoDate(to)) return null;
  const fromMs = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const toMs = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  const diff = (toMs - fromMs) / 86_400_000;
  return Number.isInteger(diff) ? diff : null;
}

/** 最近 7 天窗口（含 `referenceDate`），升序。`size <= 0` → `[]`。 */
export function recent7dDates(referenceDate: IsoDate, size: number = 7): readonly IsoDate[] {
  return recentBusinessDates(referenceDate, size);
}

// ---------------------------------------------------------------------------
// 「预期最新数据业务日」——缺口判定的时间边界（2026-10-05 第十四批）
// ---------------------------------------------------------------------------
//
// ## 为什么需要它（生产实测的真问题）
//
// 2026-10-05 00:30 生产实测：窗口右端取自**全平台最新业务日**（Shopify 当天已有
// 2026-10-05 的日指标）→ 窗口 = 09-29..10-05；而 Meta 的 T+1 日事实当时只到 10-04、
// Google 只到 10-03。于是 Meta 报了 10 条 `RECENT_GAP`「最近 7 天缺 2026-10-05」——
// 可 **10-05 就是「今天」**，当天数据本来就要等平台结算；Google 的 10-04 同理
// （它每天 05:20 跑一次、T+1，凌晨自然还没有 10-04 的数据）。这是**假警报**。
//
// ## 判定规则（唯一真相源，纯函数）
//
// `expectedLatestDataDate` = **按平台真实调度（`OnCalendar`）+ 数据滞后（T+N）推导出的
// 「此刻本应已产出的最后一个业务日」**：
//
//  1. 在**运维时区**里求出「最近一次**应该已经发生**的调度时刻」
//     （每天固定时刻 / 每小时 :MM；用真实 `OnCalendar`，不读 systemctl）；
//  2. 该调度时刻所在**业务日**减去该平台的**数据滞后天数**（`SYNC_PLATFORM_DATA_LAG_DAYS`）；
//  3. 只对 `日期 <= expectedLatestDataDate` 的窗口日判缺口；
//     晚于它的日子是「还没到预期产出时间」（`pendingDates`），**不叫缺口**。
//
// ### 为什么不直接排除「今天」
//
// 「今天」是**运维时钟**的概念，而缺口是**平台产出节奏**的概念：Shopify 每小时的订单
// 增量会让「今天」有数据，Meta 的 T+1 日事实则要等次日结算。只排除「今天」既挡不住
// 「T+1 平台在凌晨缺昨天」的假警报，也会在跨时区（店铺时区 ≠ 运维时区）时排错日子。
//
// ### 为什么不会把告警改宽（真实停摆仍报）
//
// `expectedLatestDataDate` **只由调度推导，不被「实际最后一次成功运行」下推**：
// 一个平台如果连续 N 天没跑，按调度推出的「本应产出到哪天」仍然是最近的，
// 于是它确实缺失的每一天都会被算进 `gapDates`（例：Google 停在 10-01、今天 10-05、
// 调度 T+1 → 预期 10-03 → 缺 10-02、10-03，照样报 `RECENT_GAP`）。
// 反过来说，「上一次成功运行」如果被用来**降低**预期，反而会让停摆的平台看起来正常——
// 那正是绝对不能做的事。因此这里**只把 lastRun 当作调度无法解析时的兜底**。

/**
 * 平台事实的**数据滞后**（T+N）：某天 D 的调度运行，能拿到的最新业务日 = D − N。
 *
 *  · `meta`  = 1（T+1：日事实按昨天结算；4x_daily 只是当天多次刷新，不改变 T+1 事实口径）
 *  · `google`= 1（T+1：每天 05:20 拉昨天的 campaign 日事实）
 *  · `shopify`= 0（T+0：每小时订单增量 + 当天日指标持续写入）
 *
 * ⚠️ 这是**业务口径常量**，不是可以从 `OnCalendar` 推出的东西（频率 ≠ 滞后）。
 * 改平台结算口径时必须同步改这里（单测钉住形状）。
 */
export const SYNC_PLATFORM_DATA_LAG_DAYS: Readonly<Record<SyncPlatform, number>> = {
  meta: 1,
  google: 1,
  shopify: 0,
};

/** 平台时区里的一串日历/时钟分量（`Intl.DateTimeFormat` 的解析产物）。 */
interface TimezoneParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

/** 时区格式化器的**纯记忆化缓存**（构造 `Intl.DateTimeFormat` 实测较贵）。 */
const TIMEZONE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/**
 * 取某个 IANA 时区的格式化器（缓存）；非法时区 → `null`（不抛）。
 *
 * 只做记忆化：同一个时区的输出与不缓存时逐字一致，因此不改变任何判定结果。
 */
function timezoneFormatter(timeZone: string): Intl.DateTimeFormat | null {
  const cached = TIMEZONE_FORMATTERS.get(timeZone);
  if (cached !== undefined) return cached;
  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    TIMEZONE_FORMATTERS.set(timeZone, formatter);
    return formatter;
  } catch {
    return null;
  }
}

/** 在指定 IANA 时区里拆出年/月/日/时/分/秒；非法时刻或非法时区 → `null`。 */
function timezoneParts(instantMs: number, timeZone: string): TimezoneParts | null {
  if (!Number.isFinite(instantMs)) return null;
  try {
    const formatter = timezoneFormatter(timeZone);
    if (formatter === null) return null;
    const parts = formatter.formatToParts(new Date(instantMs));
    const read = (type: string): number => {
      const found = parts.find((part) => part.type === type)?.value ?? "";
      return Number(found);
    };
    const year = read("year");
    const month = read("month");
    const day = read("day");
    // `hour12: false` 下个别运行时会回 "24" 表示午夜，归一成 0。
    const hour = read("hour") % 24;
    const minute = read("minute");
    const second = read("second");
    if (![year, month, day, hour, minute, second].every((value) => Number.isFinite(value))) return null;
    return { year, month, day, hour, minute, second };
  } catch {
    return null;
  }
}

/** 某时刻在某时区的 UTC 偏移（毫秒）；非法 → `null`。 */
function timezoneOffsetMs(instantMs: number, timeZone: string): number | null {
  const parts = timezoneParts(instantMs, timeZone);
  if (parts === null) return null;
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - instantMs;
}

/**
 * 把「时区里的墙钟时间」换成绝对时刻（毫秒；非法 → `null`）。
 *
 * 迭代两次以求在 DST 切换边界也稳定：第一次用猜测时刻的偏移，第二次用修正后的偏移复核。
 * （对 `Asia/Hong_Kong` 这类无 DST 时区，两次结果相同。）
 */
function wallClockToInstantMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number | null {
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const first = timezoneOffsetMs(guess, timeZone);
  if (first === null) return null;
  const instant = guess - first;
  const second = timezoneOffsetMs(instant, timeZone);
  if (second !== null && second !== first) return guess - second;
  return instant;
}

const HOUR_PAD = (value: number): string => String(value).padStart(2, "0");

/**
 * 一个 `OnCalendar` 在**一天里的全部触发墙钟时刻**（`HH:MM` 升序）：
 *  · `*-*-* *:MM:SS` → 00:MM … 23:MM（每小时）；
 *  · `*-*-* HH,HH:MM:SS` → 各固定时刻；
 *  · 解析不了 → `[]`（**不猜**，与展示层同一纪律）。
 */
export function scheduledTimesOfDay(onCalendar: string): readonly string[] {
  const parsed = parseOnCalendar(onCalendar);
  if (parsed.kind === "hourly") {
    const out: string[] = [];
    for (let hour = 0; hour < 24; hour += 1) out.push(`${HOUR_PAD(hour)}:${parsed.minute}`);
    return out;
  }
  if (parsed.kind === "timed") return parsed.times;
  return [];
}

/**
 * 最近一次**应该已经发生**的调度时刻（ISO-8601，UTC）——`schedule` 的**全部作业**里取最晚。
 *
 * 只看 `now` 所在运维业务日与前一天（任何受支持的 `OnCalendar` 都至少每天触发一次，
 * 因此最近一次触发必在这两天内）；找不到任何可解析的触发时刻 → `null`。
 */
export function latestScheduledRunIso(input: {
  readonly jobs: readonly SyncJobSchedule[];
  readonly nowIso: string;
  readonly opsTimeZone: string;
}): string | null {
  const nowMs = new Date(input.nowIso).getTime();
  if (!Number.isFinite(nowMs)) return null;
  const opsDate = businessDateInTimezone(input.nowIso, input.opsTimeZone);
  if (opsDate === null) return null;
  const dates = [shiftIsoDate(opsDate, -1), opsDate];
  let best: number | null = null;
  for (const job of input.jobs) {
    for (const time of scheduledTimesOfDay(job.onCalendar)) {
      const hour = Number(time.slice(0, 2));
      const minute = Number(time.slice(3, 5));
      if (!Number.isInteger(hour) || !Number.isInteger(minute)) continue;
      for (const date of dates) {
        const instant = wallClockToInstantMs(
          Number(date.slice(0, 4)),
          Number(date.slice(5, 7)),
          Number(date.slice(8, 10)),
          hour,
          minute,
          input.opsTimeZone,
        );
        if (instant === null || instant > nowMs) continue;
        if (best === null || instant > best) best = instant;
      }
    }
  }
  return best === null ? null : new Date(best).toISOString();
}

export interface ExpectedLatestDataDateInput {
  readonly platform: SyncPlatform;
  /** 观测时刻（ISO-8601），由路由用 `options.now()` 注入，测试可复现。 */
  readonly nowIso: string;
  /** 运维时区（与 systemd timer 的服务器时区一致）。 */
  readonly opsTimeZone: string;
  /** 该平台最近一次已结束运行（可空；**只在调度无法解析时兜底**）。 */
  readonly lastRunAtIso?: string | null;
  readonly schedules?: Readonly<Record<SyncPlatform, SyncPlatformSchedule>>;
  readonly dataLagDays?: Readonly<Record<SyncPlatform, number>>;
}

export interface ExpectedLatestDataDate {
  /**
   * 此刻本应已产出的最后一个业务日；`null` = 无法推导（没有可解析调度且没有运行记录）。
   * **只对 `<= date` 的窗口日判缺口**。
   */
  readonly date: IsoDate | null;
  /** 最近一次应发生的调度时刻（ISO-8601）；调度不可解析 → `null`。 */
  readonly lastDueRunAtIso: string | null;
  /** 中文一句话说明这个日期是怎么来的（可显示 / 可复核，不含 PII）。 */
  readonly reason: string;
}

/** 「预期最新数据业务日」的**唯一实现**：调度 + 数据滞后（见本段头注释的规则表）。 */
export function expectedLatestDataDate(input: ExpectedLatestDataDateInput): ExpectedLatestDataDate {
  const schedules = input.schedules ?? SYNC_PLATFORM_SCHEDULES;
  const lagTable = input.dataLagDays ?? SYNC_PLATFORM_DATA_LAG_DAYS;
  const lag = lagTable[input.platform];
  const opsToday = businessDateInTimezone(input.nowIso, input.opsTimeZone);
  if (opsToday === null || !Number.isFinite(lag)) {
    return { date: null, lastDueRunAtIso: null, reason: `无法在时区 ${input.opsTimeZone} 确定观测日` };
  }
  const schedule = schedules[input.platform];
  const lastDueRunAtIso = latestScheduledRunIso({
    jobs: schedule.jobs,
    nowIso: input.nowIso,
    opsTimeZone: input.opsTimeZone,
  });
  if (lastDueRunAtIso !== null) {
    const dueRunDate = businessDateInTimezone(lastDueRunAtIso, input.opsTimeZone);
    if (dueRunDate !== null) {
      return {
        date: shiftIsoDate(dueRunDate, -lag),
        lastDueRunAtIso,
        reason: `按 ${schedule.label} 调度最近一次应运行时刻 ${lastDueRunAtIso}（T+${String(lag)}）推导`,
      };
    }
  }
  // 调度无法解析（manual / 形态未支持）→ 才退回真实运行记录；仍然下推 lag。
  if (input.lastRunAtIso !== null && input.lastRunAtIso !== undefined) {
    const runDate = businessDateInTimezone(input.lastRunAtIso, input.opsTimeZone);
    if (runDate !== null) {
      return {
        date: shiftIsoDate(runDate, -lag),
        lastDueRunAtIso: null,
        reason: `调度不可解析，退回最近一次运行 ${input.lastRunAtIso}（T+${String(lag)}）推导`,
      };
    }
  }
  return { date: null, lastDueRunAtIso: null, reason: `${schedule.label} 没有可解析的调度与运行记录，无法推导预期数据日` };
}

/** 窗口里**已经到了预期产出时间**的业务日（`<= dueThroughDate`）；`null` → 不判缺口。 */
export function dueWindowDates(expected: readonly IsoDate[], dueThroughDate: IsoDate | null): readonly IsoDate[] {
  if (dueThroughDate === null) return [];
  return expected.filter((date) => date <= dueThroughDate);
}

/** 窗口里**还没到预期产出时间**的业务日（`> dueThroughDate`）；`null` → 全部无法判定。 */
export function pendingWindowDates(expected: readonly IsoDate[], dueThroughDate: IsoDate | null): readonly IsoDate[] {
  if (dueThroughDate === null) return [];
  return expected.filter((date) => date > dueThroughDate);
}

// ---------------------------------------------------------------------------
// 输出行（store 组装 / api 端点 / web 渲染共用同一份形状）
// ---------------------------------------------------------------------------

export interface SyncDayStatus {
  readonly date: IsoDate;
  /** 该业务日的真实行数；`0` 表示确实没有数据（不是未知）。 */
  readonly rows: number;
}

export interface SyncHistoryStatus {
  /** 最早 / 最晚业务日；无任何数据 → `null`。 */
  readonly startDate: IsoDate | null;
  readonly endDate: IsoDate | null;
  /** 实有业务日个数（非行数）。 */
  readonly days: number;
  /** 实有行数（含所有层级 / campaign）。 */
  readonly rows: number;
  /** `[start, end]` 区间里**没有任何行**的天数（缺口）。 */
  readonly gapDays: number;
  /** 关联到该店铺的**启用**账户数（Shopify = 1）。 */
  readonly accountCount: number;
  /** 其中至少有 1 行事实的账户数。 */
  readonly accountWithDataCount: number;
}

export interface SyncRecent7dStatus {
  /** 窗口右端 = 全平台最新数据业务日（不是墙钟今天；见 readme 说明）。 */
  readonly referenceDate: IsoDate;
  readonly fromDate: IsoDate;
  readonly toDate: IsoDate;
  readonly days: readonly SyncDayStatus[];
  /** 窗口内**真有事实行**（`rows > 0`）的业务日个数（不是 `days.length - gapDates.length`）。 */
  readonly presentDays: number;
  /**
   * 窗口内 `rows === 0` **且已到预期产出时间**（`date <= dueThroughDate`）的业务日（升序）。
   *
   * ⚠️ 2026-10-05 第十四批语义修正：晚于 `dueThroughDate` 的日子（「还没到该平台预期
   * 产出时间」，如 Meta 的今天 / Google 的昨天在凌晨）**不再算缺口**——它们是
   * `pendingDates`。这正是生产 10 条假 `RECENT_GAP` 的根因。
   */
  readonly gapDates: readonly IsoDate[];
  /**
   * 窗口内**还没到预期产出时间**的业务日（`date > dueThroughDate`，升序）。
   * 这些日子 `rows` 通常为 0，但那是平台节奏，不是同步缺口。
   * `dueThroughDate === null`（无法推导）→ 空数组（此时不判缺口）。
   */
  readonly pendingDates: readonly IsoDate[];
  /**
   * 该平台「此刻本应已产出的最后一个业务日」（纯函数 `expectedLatestDataDate` 推导）；
   * `null` = 无法推导（没有可解析调度与运行记录）→ 不判缺口，如实说明而不是编一个日期。
   */
  readonly dueThroughDate: IsoDate | null;
}

export interface SyncShopPlatformStatus {
  readonly shopId: string;
  readonly domain: string;
  readonly currency: string;
  readonly ianaTimezone: string | null;
  readonly platform: SyncPlatform;
  /** 该店铺在这个平台上是否已接入（有启用账户或已有事实）。 */
  readonly connected: boolean;
  readonly health: SyncPlatformHealth;
  readonly issues: readonly SyncHealthIssue[];
  readonly history: SyncHistoryStatus;
  /**
   * 最近 7 天（含全平台最新数据业务日）。**当所有平台都没有任何数据时**为 `null`：
   * 没有「最新数据业务日」就没有可解释的窗口，此时不编造一个「今天」。
   */
  readonly recent7d: SyncRecent7dStatus | null;
}

export interface SyncJobRunStatus {
  readonly jobKind: string;
  readonly status: "running" | "succeeded" | "failed" | "partial";
  readonly startedAtIso: string | null;
  readonly finishedAtIso: string | null;
  readonly rowsWritten: number | null;
  readonly errorCode: string | null;
  /**
   * `sync_runs.error_detail`（**可选的纯加法**，2026-10-05）。
   *
   * 为什么需要它：像 `META_FACT_SKIPPED_UNKNOWN_ENTITY` 这类告警的**数量**只写在
   * `error_detail` 里（「本次运行跳过 N 行（未知实体）」），页面只知道 `error_code`
   * 会看不到「跳了多少」。老构造点可以不带该字段（因此为可选），读侧一律映射。
   */
  readonly errorDetail?: string | null;
}

export interface SyncPlatformFrequency {
  readonly cadence: SyncCadence;
  readonly display: string;
  /** `timer` = systemd；`config` = 静态配置。 */
  readonly source: "timer" | "config";
}

export interface SyncPlatformStatus {
  readonly platform: SyncPlatform;
  readonly label: string;
  readonly frequency: SyncPlatformFrequency;
  readonly jobs: readonly SyncJobSchedule[];
  /**
   * 该平台最近一次**已结束**运行的时间（`sync_runs.finished_at`）。
   * Google 的作业不写 `sync_runs`（表的 `source` CHECK 只允许 meta/shopify）→ `null`，
   * 由 `lastRunSource` 说明真实来源，**绝不假装有 scheduler 状态**。
   */
  readonly lastRunAtIso: string | null;
  readonly lastRunStatus: string | null;
  /** 一句话说明 `lastRunAtIso` 的真实来源（或为空的真实原因）。 */
  readonly lastRunSource: string;
  /** 事实表里最新的业务日。 */
  readonly latestDataDate: IsoDate | null;
  /**
   * 该平台**此刻本应已产出**的最后一个业务日（纯函数 `expectedLatestDataDate` 推导；
   * 2026-10-05 第十四批）。缺口判定只对 `<= expectedDataThroughDate` 的日子生效——
   * 晚于它的日子是「平台还没到产出时间」，不是同步缺口。
   */
  readonly expectedDataThroughDate?: IsoDate | null;
  /** `expectedDataThroughDate` 的推导依据（中文一句话，可显示 / 可复核）。 */
  readonly expectedDataThroughReason?: string;
  /** 事实表里最新的**写入**时刻（真实列，见 `latestDataWriteSource`）。 */
  readonly latestDataWrittenAtIso: string | null;
  /** `latestDataWrittenAtIso` 取自哪张表的哪一列（可复核）。 */
  readonly latestDataWriteSource: string;
  readonly health: SyncPlatformHealth;
  readonly issues: readonly SyncHealthIssue[];
  readonly shops: readonly SyncShopPlatformStatus[];
  readonly runs: readonly SyncJobRunStatus[];
  /**
   * 该平台**是否已纳入运行台账**（`sync_runs`）。
   *
   *  · `tracked`：`sync_runs.source` 的 CHECK 允许该平台，`runs` 为空 = 尚未跑过；
   *  · `not_in_ledger`：**该平台目前写不进台账**（`sync_runs_source_chk` 未放宽，
   *    例如 Google 在迁移 0038 应用之前）——页面必须显示「该平台尚未纳入台账」，
   *    而不是「没有运行台账记录」（后者会被误读成「从没跑过」）。
   *
   * 2026-10-05 新增（可选，纯加法）：用户报「Google 作业不进数据同步台账」时，
   * 需要区分「从未运行」与「运行了但记不进去」。
   */
  readonly ledgerState?: "tracked" | "not_in_ledger";
  /** `ledgerState` 的人类可读原因（`not_in_ledger` 时给出可操作的修复方式）。 */
  readonly ledgerNote?: string;
  readonly shopCount: number;
  /** 该平台上有启用账户（或已有事实）的店铺数；其余店铺是「未接入」。 */
  readonly connectedShopCount: number;
  /** 最近 7 天齐全（无缺日且有数据）的店铺数。 */
  readonly recentCompleteShopCount: number;
  /** 已接入但完全没有事实的店铺数。 */
  readonly missingShopCount: number;
  /** 完全没有该平台账户的店铺数（未接入，**不等于**同步坏了）。 */
  readonly unconnectedShopCount: number;
}

// ---------------------------------------------------------------------------
// 判定规则（纯函数 + 单测；这里是唯一真相源）
// ---------------------------------------------------------------------------

export type SyncIssueCode =
  | "SHOP_NOT_CONNECTED"
  | "SHOP_NO_FACTS"
  | "ACCOUNT_NO_FACTS"
  | "RECENT_GAP"
  | "HISTORY_GAP"
  | "HISTORY_START_LATE"
  | "JOB_FAILED_RECENT"
  | "RUN_STUCK"
  | "LATEST_RUN_FAILED"
  | "DATA_BEHIND_TODAY"
  | "NO_SHOPS";

export interface SyncHealthIssue {
  readonly code: SyncIssueCode;
  /** 中文一句话（可直接显示；不含 PII / 凭证）。 */
  readonly message: string;
  /** 该问题**至少**意味着的健康档（永远不是 `ok`）。 */
  readonly health: Exclude<SyncPlatformHealth, "ok">;
  /** 相关业务日（可为 `null`）。 */
  readonly date: IsoDate | null;
}

/** 单个店铺在单个平台上的原始观测量（SQL 数出来，不做判定）。 */
export interface SyncShopObservation {
  readonly rows: number;
  readonly startDate: IsoDate | null;
  readonly endDate: IsoDate | null;
  readonly days: number;
  readonly gapDays: number;
  readonly activeAccountCount: number;
  readonly accountWithDataCount: number;
  readonly recentGapDates: readonly IsoDate[];
  /**
   * 该店铺在这个平台上是否**已接入**（有启用账户，或已有事实）。
   * `false` = 未接入 → `partial`，**不是** `missing`：把「没做 Google 广告」
   * 说成「同步坏了」是假警报。`missing` 只留给「接入了却一条事实都没有」。
   */
  readonly connected: boolean;
}

export interface SyncShopHealthContext {
  /** 该平台所有店铺里最早的起始日（判定「历史起始明显偏晚」的基准）。 */
  readonly platformEarliestStartDate: IsoDate | null;
  /** 起始日晚于基准多少天才算「明显偏晚」；默认 7。 */
  readonly historyStartLagDays?: number;
}

/** 单店铺健康 + 问题清单。规则顺序见文件末尾的规则表。 */
export function classifyShopSyncHealth(
  observation: SyncShopObservation,
  context: SyncShopHealthContext,
): { readonly health: SyncPlatformHealth; readonly issues: readonly SyncHealthIssue[] } {
  if (!observation.connected) {
    return {
      health: "partial",
      issues: [
        {
          code: "SHOP_NOT_CONNECTED",
          message: "该店铺在这个平台上没有启用账户（未接入，不代表同步失败）",
          health: "partial",
          date: null,
        },
      ],
    };
  }
  if (observation.rows <= 0 || observation.startDate === null || observation.endDate === null) {
    return {
      health: "missing",
      issues: [
        {
          code: "SHOP_NO_FACTS",
          message: "该店铺在这个平台上没有任何事实数据",
          health: "missing",
          date: null,
        },
      ],
    };
  }

  const issues: SyncHealthIssue[] = [];
  if (observation.activeAccountCount > observation.accountWithDataCount) {
    const missing = observation.activeAccountCount - observation.accountWithDataCount;
    issues.push({
      code: "ACCOUNT_NO_FACTS",
      message: `${missing}/${observation.activeAccountCount} 个启用账户没有任何事实数据（历史未回填）`,
      health: "partial",
      date: null,
    });
  }
  if (observation.recentGapDates.length > 0) {
    issues.push({
      code: "RECENT_GAP",
      message: `最近 7 天缺 ${observation.recentGapDates.length} 天（${observation.recentGapDates.join("、")}）`,
      health: "stale",
      date: observation.recentGapDates[observation.recentGapDates.length - 1] ?? null,
    });
  }
  if (observation.gapDays > 0) {
    issues.push({
      code: "HISTORY_GAP",
      message: `历史区间有 ${observation.gapDays} 天没有数据`,
      health: "partial",
      date: null,
    });
  }
  const lagDays = context.historyStartLagDays ?? 7;
  if (context.platformEarliestStartDate !== null) {
    const lateBy = daysBetweenIso(context.platformEarliestStartDate, observation.startDate);
    if (lateBy !== null && lateBy > lagDays) {
      issues.push({
        code: "HISTORY_START_LATE",
        message: `历史起始日 ${observation.startDate} 比平台最早 ${context.platformEarliestStartDate} 晚 ${lateBy} 天（未回填）`,
        health: "partial",
        date: observation.startDate,
      });
    }
  }

  let health: SyncPlatformHealth = "ok";
  for (const issue of issues) health = worstSyncHealth(health, issue.health);
  return { health, issues };
}

export interface SyncPlatformRunContext {
  /** 每个作业类型最新一次运行；`[]` = 该平台不写运行台账（Google）。 */
  readonly runs: readonly SyncJobRunStatus[];
  /** `running` 且开始时间早于「卡死阈值」的运行数。 */
  readonly stuckRunningCount: number;
  /** 卡死运行里最早的一次开始时刻。 */
  readonly oldestStuckStartedAtIso: string | null;
  /** 作业类型最新一次运行**失败**且发生在观测时刻前 N 小时内的类型名。 */
  readonly recentlyFailedJobKinds: readonly string[];
  /** `sync_runs` 里该平台最近一次**已结束**运行的终态；`null` = 没有记录。 */
  readonly latestFinishedStatus: "succeeded" | "failed" | "partial" | null;
}

/**
 * 由**数据覆盖** + **运行台账**两部分合成平台健康与问题清单。
 *
 * 平台健康 = max(最差店铺健康, 平台级问题健康)，严重度见 `SYNC_HEALTH_SEVERITY`。
 * 运行台账问题**不会**被隐藏：`failed` 的作业类型即使当天其它作业补上了，
 * 也会以 `partial` 出现在折叠行上（「有提示」是用户的硬要求）。
 */
export function classifyPlatformSyncHealth(input: {
  readonly shopResults: readonly { readonly health: SyncPlatformHealth; readonly issues: readonly SyncHealthIssue[] }[];
  readonly run: SyncPlatformRunContext;
  readonly hasShops: boolean;
  /** 全平台最新数据业务日距运维时区「今天」的天数；`>= globalStaleAfterDays` → stale。 */
  readonly daysBehindToday: number;
  readonly globalStaleAfterDays?: number;
}): { readonly health: SyncPlatformHealth; readonly issues: readonly SyncHealthIssue[] } {
  const platformIssues: SyncHealthIssue[] = [];

  if (!input.hasShops) {
    platformIssues.push({
      code: "NO_SHOPS",
      message: "没有已接入的店铺（无法判断同步覆盖）",
      health: "missing",
      date: null,
    });
  }

  const globalStaleAfter = input.globalStaleAfterDays ?? 2;
  if (input.hasShops && input.daysBehindToday >= globalStaleAfter) {
    platformIssues.push({
      code: "DATA_BEHIND_TODAY",
      message: `最新数据业务日比今天落后 ${input.daysBehindToday} 天（全平台同步可能停摆）`,
      health: "stale",
      date: null,
    });
  }

  if (input.run.recentlyFailedJobKinds.length > 0) {
    platformIssues.push({
      code: "JOB_FAILED_RECENT",
      message: `最近 24 小时内失败的作业：${input.run.recentlyFailedJobKinds.join("、")}`,
      health: "partial",
      date: null,
    });
  }

  if (input.run.stuckRunningCount > 0) {
    platformIssues.push({
      code: "RUN_STUCK",
      message: `${input.run.stuckRunningCount} 个运行停留在 running（最早开始于 ${input.run.oldestStuckStartedAtIso ?? "未知"}，可能是中断的回补）`,
      health: "partial",
      date: null,
    });
  }

  if (input.run.latestFinishedStatus === "failed") {
    platformIssues.push({
      code: "LATEST_RUN_FAILED",
      message: "该平台最近一次已结束的运行是失败",
      health: "error",
      date: null,
    });
  }

  let health: SyncPlatformHealth = "ok";
  for (const shop of input.shopResults) health = worstSyncHealth(health, shop.health);
  for (const issue of platformIssues) health = worstSyncHealth(health, issue.health);

  return {
    health,
    // 平台级问题排在前面（它们解释折叠行上的提示），随后按店铺顺序罗列。
    issues: [...platformIssues, ...input.shopResults.flatMap((shop) => shop.issues)],
  };
}

/** 平台级一句话概况（折叠行显示；纯函数，便于单测）。 */
export function summarizePlatformSync(input: {
  readonly shopCount: number;
  readonly connectedShopCount: number;
  readonly recentCompleteShopCount: number;
  readonly missingShopCount: number;
  readonly latestDataDate: IsoDate | null;
  readonly referenceDate: IsoDate | null;
}): string {
  const connected = input.connectedShopCount;
  if (input.shopCount === 0 || connected === 0) return "没有已接入的店铺";
  const unconnected = Math.max(0, input.shopCount - connected);
  const parts: string[] =
    unconnected === 0
      ? [`${input.recentCompleteShopCount}/${input.shopCount} 家店最近 7 天齐全`]
      : [`${input.recentCompleteShopCount}/${connected} 家已接入店最近 7 天齐全`, `${unconnected} 家未接入`];
  if (input.missingShopCount > 0) parts.push(`${input.missingShopCount} 家已接入但无数据`);
  if (
    input.latestDataDate !== null &&
    input.referenceDate !== null &&
    input.latestDataDate !== input.referenceDate
  ) {
    parts.push(`最新数据 ${input.latestDataDate}`);
  }
  return parts.join("，");
}

/** 供 store / 端点复用的「应有日期 − 实有日期」缺口计算。 */
export function recentGapDates(expected: readonly IsoDate[], rowsByDate: Readonly<Record<string, number>>): readonly IsoDate[] {
  return expected.filter((date) => (rowsByDate[date] ?? 0) <= 0);
}

/** `[start, end]` 区间天数（含两端）；非法 / 倒序 → `null`。 */
export function inclusiveSpanDays(startDate: IsoDate | null, endDate: IsoDate | null): number | null {
  if (startDate === null || endDate === null) return null;
  const diff = daysBetweenIso(startDate, endDate);
  if (diff === null || diff < 0) return null;
  return diff + 1;
}

/** 历史缺口 = 区间天数 − 实有天数；数据齐全时为 0，无数据时为 0（由 `missing` 档表达）。 */
export function historyGapDays(span: number | null, presentDays: number): number {
  if (span === null || span <= 0) return 0;
  return Math.max(0, span - presentDays);
}

/** 业务日加减（转出给 store / web，避免各自实现）。 */
export function shiftSyncDate(date: IsoDate, deltaDays: number): IsoDate {
  return shiftIsoDate(date, deltaDays);
}

/* ---------------------------------------------------------------------------
 * 健康判定规则表（**唯一真相源**；`classifyShopSyncHealth` /
 * `classifyPlatformSyncHealth` 逐条实现，单测 `sync-status.spec.ts` 逐条覆盖）
 *
 * 优先级 = `SYNC_HEALTH_SEVERITY`：error(4) > missing(3) > stale(2) > partial(1) > ok(0)。
 * 一个对象命中多条时取最差档，**但所有问题都保留在 `issues[]` 里**（提示不能只留一条）。
 *
 * | # | 条件                                                        | 档位      | issue 码            |
 * |---|-------------------------------------------------------------|-----------|---------------------|
 * | 0 | 该平台没有启用账户（未接入）                                 | partial   | SHOP_NOT_CONNECTED  |
 * | 1 | 已接入但该平台 0 行事实                                      | missing   | SHOP_NO_FACTS       |
 * | 2 | 有启用账户但其中 ≥1 个 0 行事实                              | partial   | ACCOUNT_NO_FACTS    |
 * | 3 | 最近 7 天窗口里 **`<= dueThroughDate` 且 `rows = 0`** 的天数 ≥1            | stale     | RECENT_GAP          |
 * | 4 | `[start, end]` 区间内有缺日                                  | partial   | HISTORY_GAP         |
 * | 5 | 起始日 > 平台最早起始日 + 7 天（可配）                       | partial   | HISTORY_START_LATE  |
 * | 6 | 某作业类型最近一次运行失败且发生在 24h 内                    | partial   | JOB_FAILED_RECENT   |
 * | 7 | 有 `running` 运行超过卡死阈值（默认 6h）                     | partial   | RUN_STUCK           |
 * | 8 | 平台最近一次已结束运行失败                                   | error     | LATEST_RUN_FAILED   |
 * | 9 | 全平台最新数据业务日比运维时区「今天」落后 ≥2 天              | stale     | DATA_BEHIND_TODAY   |
 * | 10| 平台没有已接入店铺                                           | missing   | NO_SHOPS            |
 *
 * 为什么「某作业失败」是 partial 而不是 error：`insights_daily` / `insights_roll7`
 * 按账户逐条跑，单条 `WRITER_FAILED` 不代表数据缺失（当天其它运行已补上）；
 * 只有**整个平台最近一次已结束运行失败**才升到 error。这样既提示问题，又不谎报数据坏了。
 * ------------------------------------------------------------------------- */
